import { call, select, takeLatest } from 'redux-saga/effects';
import * as subscriptionsApi from '@api/subscriptions';
import { downloadBlob } from '@utils/download';
import { actions } from './clinicalTrialSiteRecipients.slice';
import {
  BulkSubscriptionUploadResult,
  RecipientContent,
} from '@api/subscriptions';
import type {
  DownloadBulkTemplateAction,
  ExportSiteRecipientsAction,
  FetchSiteRecipientsAction,
  UploadBulkRecipientsAction,
} from './clinicalTrialSiteRecipients.types';
import {
  buildRecipientsExportFileName,
  isUploadRejected,
} from './clinicalTrialSiteRecipients.utils';
import { selectClinicalTrial } from '../clinicalTrial/clinicalTrial.slice';
import type { ClinicalTrialState } from '../clinicalTrial/clinicalTrial.types';
import { dispatch } from '../helpers';
import { runToast, showErrorToast } from '../tools';

/**
 * Toasts announce, the panel details. REJECTED is a row refusal on a 200
 * (D7-7702); ERROR is the request failing, where `detail` wins if present.
 */
const UPLOAD_SUCCESS_MESSAGE = 'Notification recipients uploaded successfully.';
const UPLOAD_REJECTED_MESSAGE = 'Some rows in the file were rejected.';
const UPLOAD_ERROR_MESSAGE = 'Notification recipients could not be uploaded.';
const TEMPLATE_FILE_NAME = 'notification-recipients-template.csv';
const TEMPLATE_ERROR_MESSAGE = 'The CSV template could not be downloaded.';
const EXPORT_ERROR_MESSAGE = 'Notification recipients could not be exported.';

/**
 * One site at a time — `siteId` is a required query param, so the page does
 * not dispatch this until a site is picked.
 */
export function* fetchSiteRecipientsWorker(action: FetchSiteRecipientsAction) {
  const { clinicalTrialId, siteId, search, notificationTypes } = action.payload;

  try {
    const recipients: RecipientContent[] = yield call(
      subscriptionsApi.fetchRecipients,
      clinicalTrialId,
      { siteId, search, notificationTypes }
    );

    yield dispatch(actions.fetchSiteRecipientsSuccess(recipients ?? []));
  } catch (error) {
    yield dispatch(actions.fetchSiteRecipientsFailure(error as Error));
  }
}

/**
 * Three outcomes, not two — "request worked" ≠ "rows added" (D7-7702). The
 * full result is dispatched on both 2xx paths for D7-7650's panel.
 */
export function* uploadBulkRecipientsWorker(
  action: UploadBulkRecipientsAction
) {
  const { clinicalTrialId, file } = action.payload;

  try {
    const result: BulkSubscriptionUploadResult = yield call(
      subscriptionsApi.bulkUploadSubscriptions,
      clinicalTrialId,
      file
    );

    if (isUploadRejected(result)) {
      yield dispatch(actions.uploadBulkRecipientsRejected(result));

      yield call(runToast, 'error', UPLOAD_REJECTED_MESSAGE);

      return;
    }

    yield dispatch(actions.uploadBulkRecipientsSuccess(result));

    yield call(runToast, 'success', UPLOAD_SUCCESS_MESSAGE);
  } catch (error) {
    yield dispatch(actions.uploadBulkRecipientsFailure(error as Error));

    // NOTE: `yield*`, not `yield`, so this worker yields the toast effect and
    // the effect-sequence test can assert it. Other sagas mock `tools`.
    yield* showErrorToast(error, UPLOAD_ERROR_MESSAGE);
  }
}

/**
 * No success toast — the downloaded file is confirmation. `downloadBlob`, not
 * `downloadCSV`, which would prepend a BOM to already-encoded bytes.
 */
export function* downloadBulkTemplateWorker(
  action: DownloadBulkTemplateAction
) {
  const { clinicalTrialId } = action.payload;

  try {
    const template: Blob = yield call(
      subscriptionsApi.downloadBulkUploadTemplate,
      clinicalTrialId
    );

    yield call(downloadBlob, TEMPLATE_FILE_NAME, template);

    yield dispatch(actions.downloadBulkTemplateSuccess());
  } catch (error) {
    yield dispatch(actions.downloadBulkTemplateFailure(error as Error));

    yield* showErrorToast(error, TEMPLATE_ERROR_MESSAGE);
  }
}

/**
 * Same handling as the template above — the backend writes the CSV, so its
 * bytes reach the browser untouched and only a failure is announced. The name
 * it is saved under is ours, though: see `buildRecipientsExportFileName`.
 */
export function* exportSiteRecipientsWorker(
  action: ExportSiteRecipientsAction
) {
  const { clinicalTrialId, siteId, search, notificationTypes } = action.payload;

  try {
    const csv: Blob = yield call(
      subscriptionsApi.exportRecipients,
      clinicalTrialId,
      { siteId, search, notificationTypes }
    );

    const { data: clinicalTrial }: ClinicalTrialState =
      yield select(selectClinicalTrial);

    const fileName: string = yield call(buildRecipientsExportFileName, {
      studyCode: clinicalTrial?.code,
      siteId,
      sites: clinicalTrial?.sites,
    });

    yield call(downloadBlob, fileName, csv);

    yield dispatch(actions.exportSiteRecipientsSuccess());
  } catch (error) {
    yield dispatch(actions.exportSiteRecipientsFailure(error as Error));

    yield* showErrorToast(error, EXPORT_ERROR_MESSAGE);
  }
}

export function* clinicalTrialSiteRecipientsSaga() {
  yield takeLatest(actions.fetchSiteRecipients.type, fetchSiteRecipientsWorker);
  yield takeLatest(
    actions.uploadBulkRecipients.type,
    uploadBulkRecipientsWorker
  );
  yield takeLatest(
    actions.downloadBulkTemplate.type,
    downloadBulkTemplateWorker
  );
  yield takeLatest(
    actions.exportSiteRecipients.type,
    exportSiteRecipientsWorker
  );
}
